import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Plus, Trash2, Save, RotateCcw, ListChecks, Flag, Target } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * TaskTemplateSection — Settings-owned editor for the "Seed default UK
 * timeline" that every project can apply from the Tasks tab. Admins /
 * managers can rename items, adjust start / end day offsets, toggle
 * milestone / goal flags, reorder via drag handles, and reset to the
 * built-in defaults.
 */
export default function TaskTemplateSection() {
  const [items, setItems] = useState([]);
  const [usingDefault, setUsingDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/task-template");
      setItems((data.items || []).map((it, i) => ({ ...it, _uid: `t-${i}-${Date.now()}` })));
      setUsingDefault(!!data.using_default);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load template");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (uid, key, val) =>
    setItems(prev => prev.map(it => it._uid === uid ? { ...it, [key]: val } : it));
  const remove = (uid) => setItems(prev => prev.filter(it => it._uid !== uid));
  const add = () =>
    setItems(prev => [...prev, {
      _uid: `t-new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: "", start_offset_days: 0, end_offset_days: 1,
      milestone: false, goal: false,
    }]);

  const onDragStart = (uid) => (e) => {
    setDragId(uid);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", uid); } catch {}
  };
  const onDragOver = (uid) => (e) => { if (dragId) e.preventDefault(); };
  const onDrop = (uid) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === uid) return;
    setItems(prev => {
      const next = [...prev];
      const from = next.findIndex(x => x._uid === dragId);
      const to = next.findIndex(x => x._uid === uid);
      if (from < 0 || to < 0) return prev;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDragId(null);
  };
  const onDragEnd = () => setDragId(null);

  const save = async () => {
    // Basic client-side validation — name required, non-negative offsets,
    // end ≥ start. Backend also normalises but a friendly error here beats
    // a 422 stack trace.
    for (const [i, it] of items.entries()) {
      if (!(it.name || "").trim()) { toast.error(`Row ${i + 1}: name is required`); return; }
      if (Number(it.end_offset_days) < Number(it.start_offset_days)) {
        toast.error(`Row ${i + 1}: end day must be ≥ start day`); return;
      }
    }
    setBusy(true);
    try {
      await api.put("/settings/task-template", {
        items: items.map(({ _uid, ...it }) => ({
          name: it.name.trim(),
          start_offset_days: Number(it.start_offset_days) || 0,
          end_offset_days: Number(it.end_offset_days) || 0,
          milestone: !!it.milestone,
          goal: !!it.goal,
        })),
      });
      toast.success("Timeline template saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the timeline template to the built-in UK defaults? Your customisations will be lost.")) return;
    setBusy(true);
    try {
      await api.post("/settings/task-template/reset");
      toast.success("Template reset to defaults");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="swiss-card-flat p-6" data-testid="task-template-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ListChecks size={14} />
            <div className="label">Default project timeline</div>
            {usingDefault && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded"
                    style={{ background: "#0F8B8420", color: "#0F8B84" }}
                    data-testid="task-template-using-default">
                Built-in defaults
              </span>
            )}
          </div>
          <div className="font-display text-lg font-semibold">Seed default UK timeline — editable</div>
          <p className="muted text-xs mt-1">
            Rename tasks, tune day offsets, toggle milestone / goal, and reorder via the drag handle.
            Any project's Tasks tab &rarr; <em>Seed default UK timeline</em> button stamps this list out.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={reset} disabled={busy || usingDefault} className="btn-ghost"
                  data-testid="task-template-reset-btn"
                  title="Drop customisations and use the built-in UK defaults">
            <RotateCcw size={12} /> Reset
          </button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="task-template-save-btn">
            <Save size={13} /> {busy ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="muted text-xs font-mono uppercase tracking-widest">Loading template…</div>
      ) : (
        <div className="space-y-1" data-testid="task-template-list">
          <div className="grid grid-cols-[24px_2fr_80px_80px_120px_40px] gap-2 items-center text-[10px] uppercase tracking-widest muted px-2 pb-1">
            <span></span>
            <span>Task name</span>
            <span className="text-right">Start day</span>
            <span className="text-right">End day</span>
            <span>Flags</span>
            <span></span>
          </div>
          {items.map((it) => (
            <div
              key={it._uid}
              draggable
              onDragStart={onDragStart(it._uid)}
              onDragOver={onDragOver(it._uid)}
              onDrop={onDrop(it._uid)}
              onDragEnd={onDragEnd}
              className={`grid grid-cols-[24px_2fr_80px_80px_120px_40px] gap-2 items-center p-2 border ${
                dragId === it._uid ? "opacity-40 border-[var(--accent-hover)]" : "border-[var(--line)]"
              } bg-white`}
              data-testid={`task-template-row-${it._uid}`}
            >
              <span className="cursor-grab active:cursor-grabbing muted" title="Drag to reorder"
                    data-testid={`task-template-drag-${it._uid}`}>
                <GripVertical size={13} />
              </span>
              <input
                className="input-swiss"
                value={it.name}
                onChange={(e) => upd(it._uid, "name", e.target.value)}
                placeholder="e.g. Site survey"
                data-testid={`task-template-name-${it._uid}`}
              />
              <input
                type="number" min="0" className="input-swiss text-right font-mono"
                value={it.start_offset_days}
                onChange={(e) => upd(it._uid, "start_offset_days", e.target.value)}
                data-testid={`task-template-start-${it._uid}`}
              />
              <input
                type="number" min="0" className="input-swiss text-right font-mono"
                value={it.end_offset_days}
                onChange={(e) => upd(it._uid, "end_offset_days", e.target.value)}
                data-testid={`task-template-end-${it._uid}`}
              />
              <div className="flex items-center gap-3 text-xs">
                <label className="flex items-center gap-1 cursor-pointer" title="Milestone">
                  <input type="checkbox" checked={!!it.milestone}
                         onChange={(e) => upd(it._uid, "milestone", e.target.checked)}
                         data-testid={`task-template-milestone-${it._uid}`} />
                  <Flag size={11} /> M
                </label>
                <label className="flex items-center gap-1 cursor-pointer" title="Goal">
                  <input type="checkbox" checked={!!it.goal}
                         onChange={(e) => upd(it._uid, "goal", e.target.checked)}
                         data-testid={`task-template-goal-${it._uid}`} />
                  <Target size={11} /> G
                </label>
              </div>
              <button type="button" onClick={() => remove(it._uid)}
                      className="btn-ghost text-red-700 hover:text-red-800"
                      data-testid={`task-template-remove-${it._uid}`}
                      title="Remove row">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button onClick={add} className="btn-ghost mt-2" data-testid="task-template-add-btn">
            <Plus size={12} /> Add row
          </button>
        </div>
      )}
      <div className="text-[11px] muted mt-3">
        Day offsets are counted from the moment "Seed default UK timeline" is clicked on a project.
        E.g. start = 0 &amp; end = 3 means "starts today, ends in 3 days".
      </div>
    </section>
  );
}
